/*
 * @Description: 重写captureException
 * @Author: 彭锦
 * @Date: 2021-02-26 13:48:53
 * @LastEditors: pengjin
 * @LastEditTime: 2022-10-10 16:05:09
 */

import * as Sentry from '@sentry/react';
import { AxiosResponse } from 'axios';
import { Result } from '../../../response';
import { isError, isObject } from './util';

export enum NameMenu {
  PromiseError = 'PromiseError',
  InterfaceError = 'InterfaceError',
  LoadSourceError = 'LoadSourceError',
  AsyncError = 'AsyncError',
  CorsError = 'CorsError',
  InterfaceWarn = 'InterfaceWarn',
  Error = 'Error',
}

interface ExceptionOption {
  tags?: {
    errorType?: number;
    errorObj?: Error;
    apiUrl?: string;
    errorLevel?: string;
    sourceUrl?: string;
  };
  extra?: {
    [x: string]: any;
    apiTime?: number;
    params?: any;
    respCode?: number | string;
    respData?: any;
    errorNative?: any;
  };
}

interface InitOptions {
  logErrors: boolean;
}

interface InterfaceExceptionOption {
  errorType?: number;
  errorObj?: any;
  respCode?: number;
  respData?: any;
  params?: any;
  apiUrl?: string;
  apiTime?: number;
  errorLevel?: string;
}

export enum SentryErrorType {
  PromiseError = 1,
  PageError = 2,
  InterfaceError = 3,
  LoadSourceError = 4,
  AsyncError = 5,
  CorsError = 6,
  ComponentError = 7,
}

/**
 * 定义自己的错误类型
 * @param {*} message 错误信息
 * @param {*} name 错误类型
 * NameMenu = ['PromiseError', 'InterfaceError', 'LoadSourceError', 'AsyncError', 'CorsError', 'InterfaceWarn']
 */
export class CustomerError extends Error {
  constructor(message: string, name: NameMenu) {
    super(name);
    this.message = message;
    // v8环境才有
    // Error.captureStackTrace(this,CustomerError);
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    Object.prototype.hasOwnProperty.call(Error, 'captureStackTrace')
      ? Error.captureStackTrace(this, CustomerError)
      : (this.stack = new Error().stack);
  }
}

type LimitErrorCnt<T> = {
  [K in keyof T]: number;
};

/**
 * 封装上报错误接口
 * @param {*} err 可以是Error、Object、String
 * @param {*} type 错误类型
 * @param {*} opts 其他信息如tag/extra
 * @param {*} limitErrorCnt 报错发送邮件次数
 */
export const captureException = (
  err: any,
  type: SentryErrorType,
  opts: ExceptionOption,
  limitErrorCnt?: LimitErrorCnt<number> | number
) => {
  let errObj: any;
  if (err instanceof Error) {
    errObj = err;
  } else if (err instanceof Object) {
    errObj = new Error(err.message);
    Object.keys(err).forEach((key) => {
      errObj[key] = err[key];
    });
  } else if (typeof err === 'string') {
    errObj = new Error(err);
  }
  let errorType;
  let isSendEmail = 0;
  const errorCnt: {
    [K in SentryErrorType]?: number;
  } = {};
  if (!errorCnt[type]) {
    errorCnt[type] = 0;
  }
  switch (type) {
    case SentryErrorType.PromiseError:
      errorType = 'promise错误';
      isSendEmail = 1;
      errObj.name = 'PromiseError';
      break;
    case SentryErrorType.PageError:
      errorType = '页面级错误';
      isSendEmail = 1;
      errObj.name = 'PageError';
      break;
    case SentryErrorType.InterfaceError:
      errorType = '接口错误';
      isSendEmail = 1;
      errObj.name = 'InterfaceError';
      break;
    case SentryErrorType.LoadSourceError:
      errorType = '加载资源错误';
      break;
    case SentryErrorType.AsyncError:
      errorType = '异步错误';
      isSendEmail = 1;
      errObj.name = 'AsyncError';
      break;
    case SentryErrorType.CorsError:
      errorType = '跨域脚本错误';
      break;
    case SentryErrorType.ComponentError:
      errorType = '组件级错误';
      errObj.name = 'ComponentError';
      break;
    default:
      break;
  }
  if (
    [
      SentryErrorType.PromiseError,
      SentryErrorType.PageError,
      SentryErrorType.InterfaceError,
      SentryErrorType.AsyncError,
    ].indexOf(type) < 0
  ) {
    ++(errorCnt[type] as number);
    const limit = typeof limitErrorCnt === 'object' ? limitErrorCnt[type] || 3 : limitErrorCnt || 3;
    isSendEmail = (errorCnt[type] as number) > limit ? 1 : 0;
  }
  const tagsDefault = {
    errorType,
    errInfo: errObj && errObj.message,
    env: import.meta.env.VITE_APP_ENV || 'unknown',
    isSendEmail,
    href: window.location.href,
    // issueOwner: '15815577564'
  };
  // const date = parseTime(new Date(), '{y}-{m}-{d}');
  Sentry.captureException(errObj, {
    // @ts-ignore
    tags: Object.assign(tagsDefault, opts.tags || {}),
    // extra: {
    //   ...(opts.extra || {}),
    // },
    // fingerprint: [date, import.meta.env.VITE_APP_ENV, errorType, errObj && errObj.message],
  });
};

/**
 * 接口报错上报
 * @param {*} opts
 */
export const catchError = (opts: InterfaceExceptionOption) => {
  const {
    errorType = SentryErrorType.InterfaceError,
    errorObj,
    respCode,
    respData,
    params,
    apiUrl,
    apiTime,
    errorLevel,
  } = opts;
  const option = {
    tags: {
      errorType,
      apiUrl,
      errorLevel,
    },
    extra: {
      apiTime,
      params,
      respCode,
      respData,
    },
  };
  captureException(errorObj, errorType, option, 3);
};

export const captureInterfaceError = (
  response: AxiosResponse<Result>,
  opt: { apiTime: number; message: string; errorLevel: string }
) => {
  const { status, data, config } = response;
  catchError({
    errorType: opt.errorLevel === 'error' ? SentryErrorType.InterfaceError : 8,
    errorLevel: opt.errorLevel,
    errorObj: new CustomerError(
      opt.message,
      opt.errorLevel === 'error' ? NameMenu.InterfaceError : NameMenu.InterfaceWarn
    ),
    respCode: status,
    respData: data,
    params: config.method!.toLocaleLowerCase() === 'get' ? config.params : config.data,
    apiUrl: config.url,
    apiTime: opt.apiTime,
  });
};

export const awaitWrap = (promise: Promise<any>) =>
  promise
    .then((res) => [null, res])
    .catch((err) => {
      captureException(err, SentryErrorType.AsyncError, {});
      return [err, null];
    });

// 资源加载白名单
const sourceLoadWhiteList = [
  'bat.bing.com',
  'connect.facebook.net',
  'www.clarity.ms',
  'www.googletagmanager.com',
  'trc.taboola.com',
];

/**
 * 资源错误上报
 */
function captureSourceLoad() {
  window.addEventListener(
    'error',
    (event) => {
      // 过滤js error
      const target = event.target || event.srcElement;
      const isElementTarget =
        target instanceof HTMLScriptElement ||
        target instanceof HTMLLinkElement ||
        target instanceof HTMLImageElement;
      if (!isElementTarget) return false;
      // 上报资源地址
      const url =
        (target as HTMLScriptElement | HTMLImageElement).src || (target as HTMLLinkElement).href;
      if (sourceLoadWhiteList.some((item) => url.includes(item))) return false;
      const err = new CustomerError(
        `${(target as HTMLElement).tagName} 资源 ${url} 加载错误`,
        NameMenu.LoadSourceError
      );
      captureException(err, SentryErrorType.LoadSourceError, {
        tags: {
          sourceUrl: url,
        },
      });
    },
    true
  );
}

/**
 * 拦截console.error
 */
function proxyConsoleError() {
  const nativeConsoleError = window.console.error;
  window.console.error = function (...args: any[]) {
    console.log(...args);
    args.forEach((item) => {
      let errorObj;
      if (isObject(item) || isError(item)) {
        errorObj = {
          message: item.message || '',
          stack: item.stack || (item.error && item.error.stack),
          errorNative: item,
        };
      } else {
        errorObj = {
          message: JSON.stringify(item),
          stack: JSON.stringify(item),
          errorNative: item,
        };
      }
      captureException(errorObj, SentryErrorType.PageError, {
        extra: {
          errorNative: errorObj.errorNative,
        },
      });
    });
    nativeConsoleError.apply(this, args);
  };
}

export function init(options: InitOptions) {
  if (options.logErrors) {
    proxyConsoleError();
  }
  captureSourceLoad();
}
